import { useAppDispatch, useAppSelector } from '@store/hooks';
import { onModalCloseRequest } from '@store/modal/thunks';
import { FC, useCallback, useMemo, useState } from 'react';

import AutoComplete, { OptionType } from '../AutoComplete';
import Modal, { Sizes } from '../Modal';

import DemoRequestForm from './components/DemoRequestForm';
import FeedbackForm from './components/FeedbackForm';
import TaskRequestForm from './components/TaskRequestForm';
import {
  CHANGE_REQUEST_MODAL_ID,
  DEMO_REQUEST_TYPE,
  FEEDBACK_REQUEST_TYPE,
  REQUEST_TYPE_OPTIONS,
} from './constants';
import {
  Container,
  EmptyStateHint,
  ReportedByHint,
  RequestTypeContainer,
} from './styles';
import { Props } from './types';
import { getOptionValue } from './utils';

const ChangeRequestModal: FC<Props> = ({
  className,
  id = CHANGE_REQUEST_MODAL_ID,
}) => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const [requestType, setRequestType] = useState<OptionType | null>(null);

  const requestTypeValue = useMemo(
    () => (requestType ? getOptionValue(requestType) : ''),
    [requestType],
  );

  const resetForm = useCallback(() => setRequestType(null), []);

  const handleClose = useCallback(() => {
    resetForm();
    dispatch(onModalCloseRequest({ id }));
  }, [dispatch, id, resetForm]);

  return (
    <Modal
      className={className}
      id={id}
      size={Sizes.medium}
      renderTitle={() => 'Help Request'}
      onClose={resetForm}
      renderChildren={() => (
        <Container>
          <ReportedByHint>
            Reported by: {user?.userName || user?.fullName || '—'}
          </ReportedByHint>
          <RequestTypeContainer>
            <AutoComplete
              name="request_type"
              label="Request Type"
              options={REQUEST_TYPE_OPTIONS}
              placeholder="Select a request type"
              value={requestType}
              onChange={setRequestType}
              fullWidth
            />
          </RequestTypeContainer>
          {!requestTypeValue && (
            <EmptyStateHint>
              Select a request type to tell us what you need.
            </EmptyStateHint>
          )}
          {requestTypeValue === FEEDBACK_REQUEST_TYPE && (
            <FeedbackForm onCancel={handleClose} onSubmitted={handleClose} />
          )}
          {requestTypeValue === DEMO_REQUEST_TYPE && (
            <DemoRequestForm onCancel={handleClose} onSubmitted={handleClose} />
          )}
          {!!requestTypeValue &&
            requestTypeValue !== FEEDBACK_REQUEST_TYPE &&
            requestTypeValue !== DEMO_REQUEST_TYPE && (
              <TaskRequestForm
                requestType={requestTypeValue}
                onCancel={handleClose}
                onSubmitted={handleClose}
              />
            )}
        </Container>
      )}
    />
  );
};

export default ChangeRequestModal;
